/**
 * Leads Tab Component for Admin Panel
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Mail, UserCheck, TrendingUp, Loader2, Inbox } from 'lucide-react';
import { getLeadStatusBadge } from './adminHelpers';

// Sortable header component - defined outside to avoid re-creation during render
const SortableHeader = ({ label, sortKey, sortConfig, onSort }) => (
  <th 
    className="text-left py-2 px-2 font-medium text-zinc-600 cursor-pointer hover:bg-zinc-100 select-none"
    onClick={() => onSort(sortKey)}
  >
    <div className="flex items-center gap-1">
      {label}
      {sortConfig.key === sortKey && (
        <span className="text-amber-500">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
      )}
    </div>
  </th>
);

export const LeadsTab = ({
  leads,
  leadsStats,
  leadsLoading,
  leadStatusFilter,
  setLeadStatusFilter,
  updateLeadStatus
}) => {
  const [sortConfig, setSortConfig] = React.useState({ key: 'created_at', direction: 'desc' });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortData = (data) => {
    if (!sortConfig.key || !data) return data;
    return [...data].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      if (sortConfig.key.includes('created_at') || sortConfig.key.includes('_at')) {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      } else if (typeof aVal === 'number') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const filteredLeads = leadStatusFilter === 'all' 
    ? leads 
    : leads.filter(l => l.status === leadStatusFilter);

  const sortedLeads = sortData(filteredLeads);

  return (
    <>
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Leads', value: leadsStats.total || 0, color: 'border-blue-500', textColor: 'text-blue-700', icon: Users },
          { label: 'New Leads', value: leadsStats.new || 0, color: 'border-amber-500', textColor: 'text-amber-700', icon: Mail },
          { label: 'Converted', value: leadsStats.converted || 0, color: 'border-green-500', textColor: 'text-green-700', icon: UserCheck },
          { label: 'Conversion', value: leadsStats.conversion_rate || '0%', color: 'border-purple-500', textColor: 'text-purple-700', icon: TrendingUp }
        ].map((stat, i) => (
          <Card key={i} className={`border-l-4 ${stat.color} bg-white`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-500 text-sm">{stat.label}</p>
                  <p className={`text-3xl font-bold ${stat.textColor}`}>{stat.value}</p>
                </div>
                <stat.icon className={`w-10 h-10 ${stat.textColor} opacity-40`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">All Leads</CardTitle>
            <select value={leadStatusFilter} onChange={(e) => setLeadStatusFilter(e.target.value)} className="text-xs border rounded px-2 py-1">
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="converted">Converted</option>
              <option value="lost">Lost</option>
            </select>
          </div>
        </CardHeader>
        <CardContent>
          {leadsLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-amber-600" /></div>
          ) : filteredLeads.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <Inbox className="w-12 h-12 mx-auto mb-3 text-zinc-300" />
              <p>No leads found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <SortableHeader label="Name" sortKey="name" sortConfig={sortConfig} onSort={handleSort} />
                    <SortableHeader label="Email" sortKey="email" sortConfig={sortConfig} onSort={handleSort} />
                    <SortableHeader label="Company" sortKey="company" sortConfig={sortConfig} onSort={handleSort} />
                    <SortableHeader label="Source" sortKey="source" sortConfig={sortConfig} onSort={handleSort} />
                    <SortableHeader label="Visits" sortKey="visit_count" sortConfig={sortConfig} onSort={handleSort} />
                    <SortableHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={handleSort} />
                    <SortableHeader label="Date" sortKey="created_at" sortConfig={sortConfig} onSort={handleSort} />
                    <th className="text-left py-2 px-2 font-medium text-zinc-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedLeads.map(lead => (
                    <tr key={lead.lead_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-2 px-2 font-medium">{lead.name}</td>
                      <td className="py-2 px-2"><a href={`mailto:${lead.email}`} className="text-amber-600 hover:underline">{lead.email}</a></td>
                      <td className="py-2 px-2 text-zinc-600">{lead.company || '-'}</td>
                      <td className="py-2 px-2"><span className="px-2 py-0.5 bg-zinc-100 rounded text-xs">{lead.source || 'website'}</span></td>
                      <td className="py-2 px-2">{lead.visit_count || 1}</td>
                      <td className="py-2 px-2">{getLeadStatusBadge(lead.status)}</td>
                      <td className="py-2 px-2 text-xs text-zinc-500">{new Date(lead.created_at).toLocaleDateString()}</td>
                      <td className="py-2 px-2">
                        <select value={lead.status} onChange={(e) => updateLeadStatus(lead.lead_id, e.target.value)} className="text-xs border rounded px-2 py-1">
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="converted">Converted</option>
                          <option value="lost">Lost</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default LeadsTab;
